const PLACEHOLDER = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;
/**
 * A pure business rule, so it needs no port and no test double.
 *
 * There is exactly one implementation. The predecessor had `personalizeHtml`
 * defined twice, free to diverge, with no way to tell which one had run.
 */
export function renderMessage(template, context) {
    const values = {
        ...context.personalization,
        email: context.recipient.email,
        name: context.recipient.name,
        unsubscribe_url: context.unsubscribeUrl ?? '',
    };
    return {
        to: context.recipient.email,
        from: template.fromEmail,
        subject: fill(template.subject, values, plain),
        // Merge values are escaped on the way into the HTML part, and only there.
        //
        // A contact's name comes from a file somebody uploaded, so it is the one
        // string in a campaign the sender did not write. `Tom & Jerry` produces
        // invalid markup, and a name holding a tag produces markup the sender
        // never wrote inside a message signed with their own domain. The subject
        // and the text part are not markup, so escaping those would show the
        // recipient a literal `&amp;`.
        html: fill(template.html, values, escapeHtml),
        // A plain-text alternative on every send. HTML-only mail is a
        // deliverability defect, so the type makes it impossible to omit.
        text: fill(template.text, values, plain),
        headers: {
            'Message-ID': context.messageId,
            // Every adapter forwards this map to its provider verbatim, so one entry
            // here reaches all six.
            ...(template.replyTo === undefined ? {} : { 'Reply-To': template.replyTo }),
            // RFC 8058 one-click unsubscribe. Mailbox providers now require it for
            // bulk senders, and its absence costs inbox placement outright — so it is
            // omitted only where there is genuinely nothing to leave, which is what
            // an absent url means here.
            ...(context.unsubscribeUrl === undefined
                ? {}
                : {
                    'List-Unsubscribe': `<${context.unsubscribeUrl}>`,
                    'List-Unsubscribe-Post': 'List-Unsubscribe=One-Click',
                }),
        },
        kind: context.kind,
        idempotencyKey: context.idempotencyKey,
    };
}
function fill(input, values, encode) {
    return input.replace(PLACEHOLDER, (_match, key) => encode(values[key] ?? ''));
}
/** For the parts that are not markup, where escaping would be visible to a reader. */
function plain(value) {
    return value;
}
/**
 * Enough to stop text being read as markup.
 *
 * Exported because two different inputs need it and there must not be two
 * copies: this file escapes a *contact's* merge value at render time, and
 * `campaignEmailTemplate` escapes what the *sender* typed when it builds the
 * body. Both end up in the same document, and neither covers the other's case.
 *
 * The quotes matter as much as the angle brackets: a value substituted inside
 * an attribute — the unsubscribe link's `href` is one — escapes that attribute
 * with a quote just as easily as it escapes an element with a bracket.
 */
export function escapeHtml(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
//# sourceMappingURL=messageTemplate.js.map